import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import equals from 'ramda/src/equals';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import queryString from 'query-string';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import AppActions from '~/actions/app';
import ManageActions from '~/actions/manage';
import { CheckoutReportsTabs } from '~/services/config';
import AverageScore from './AverageScore';
import EmployeeList from './EmployeeList';
import OrgChart from './OrgChart';

const moment = extendMoment(originalMoment);

const CheckoutReport = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const selectedCompanyId = useSelector((state) => state.app.selectedCompany);
    const companies = useSelector((state) => state.app.companies);
    const employees = useSelector((state) => state.app.users);
    const companyCheckouts = useSelector((state) => state.manage.companyCheckouts);
    const actuals = useSelector((state) => state.manage.checkoutActuals);
    const parsedCompanyIds = equals([-1], selectedCompanyId) ? companies.map((e) => e.id) : selectedCompanyId;
    const selectedCompany = find(propEq('id', parsedCompanyIds[0]))(companies);

    const [companyId, setCompanyId] = useState(parsedCompanyIds);
    const [userId, setUserId] = useState(employees.map((e) => e.id));
    const [dateStart, setDateStart] = useState(moment().subtract(2, 'day').format('YYYY-MM-DD'));
    const [dateEnd, setDateEnd] = useState(moment().subtract(1, 'day').format('YYYY-MM-DD'));
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(selectedCompany?.users?.length || 25);
    const [active, setActive] = useState(queryString.parse(location.search)?.active ?? 'chart');

    const handleTab = (e) => {
        if (e.name === active) return;
        if (e.name !== 'employees') {
            dispatch(ManageActions.getcheckoutorgchartRequest({ companyId, date: dateStart }));
        }
        navigate({ search: `?active=${e.name}` });
        setActive(e.name);
    };

    const handleDate = (value) => {
        const start = moment(value).subtract(1, 'day').format('YYYY-MM-DD');
        const end = moment(value).format('YYYY-MM-DD');
        setDateStart(start);
        setDateEnd(end);
    };

    const handleDetail = (scorecard) => (e) => {
        e.stopPropagation();
        if (scorecard) {
            const date = moment.range(dateStart, dateEnd);
            const payload = { type: 'Scorecard Detail', data: { before: { scorecard, date } } };
            dispatch(AppActions.modalRequest(payload));
        }
    };

    const handleFetch = (companyId, userId, page, perPage, dateStart, dateEnd, order = 'name', sort = 'ASC') => {
        const payload = {
            companyId,
            userId,
            page,
            perPage,
            dateStart,
            dateEnd,
            order,
            sort,
        };
        dispatch(ManageActions.getcompanycheckoutsRequest(payload));
    };

    const handleFilter = (type, e) => {
        if (type === 'company') {
            const companyIds = e.map((item) => item.id);
            dispatch(ManageActions.getcheckoutorgchartRequest({ companyId: companyIds, date: dateStart }));
        } else if (type === 'employee') {
            const userIds = e.map((item) => item.id);
            // userIds[0] < 0 = all employee option
            setUserId(userIds[0] < 0 ? employees.map((e) => e.id) : userIds);
        }
    };

    const handleSelectMenu = useCallback(
        (data) => (e) => {
            switch (e) {
                case 'start review':
                    {
                        const startOfMonth = moment(dateStart).startOf('month').format('YYYY-MM-DD');
                        const userId = data?.user?.id;
                        const companyId = data?.company?.id;
                        const payload = {
                            companyId: [companyId],
                            userId: [userId],
                            page,
                            perPage,
                            dateStart: startOfMonth,
                            dateEnd,
                            order: 'name',
                            sort: 'ASC',
                        };
                        dispatch(
                            ManageActions.getcompanycheckoutsRequest(payload, {
                                pathname: `/hcm/report-checkout/${userId}`,
                                search: `dateStart=${startOfMonth}`,
                                state: { lastReview: data?.last_review },
                            })
                        );
                    }
                    break;
            }
        },
        [dateStart, dateEnd]
    );

    const handlePerformanceReport = (item) => (e) => {
        e.stopPropagation();
    };

    const handleRowClick = (item, type) => () => {
        handleSelectMenu(item)(type);
    };

    const handleSortEmployees = (e) => {
        const { order, sort } = e;
        handleFetch(companyId, page, perPage, dateStart, dateEnd, order, sort);
    };

    useEffect(() => {
        if (active === 'chart') {
            dispatch(ManageActions.getcheckoutorgchartRequest({ companyId, date: dateStart }));
        } else {
            handleFetch(companyId, userId, page, perPage, dateStart, dateEnd);
        }
    }, [dateStart, dateEnd, companyId, userId, page, perPage, active]);

    const date = moment.range(dateStart, dateEnd);
    const data = companyCheckouts || {};

    return (
        <ErrorBoundary className="vdmm-cp h-100">
            <div className="page-filter">
                <Filter defaultIds={[[], [-1]]} withAllEmployees onChange={handleFilter} />
            </div>
            <div className="card">
                <div className="d-h-between">
                    <span className="dsl-b22 bold">Checkout Reports</span>
                    <div className="d-flex">
                        <DatePicker calendar="day" append="caret" format="DD MMM YY" as="span" align="right" value={moment(dateEnd)} onSelect={handleDate} />
                    </div>
                </div>
                <ResponsiveTabs active={active} data={CheckoutReportsTabs} getId={(e) => e.name} getValue={(e) => e.label} onTab={handleTab} />
                <Tab eventKey="employees" active={active}>
                    <AverageScore
                        className="mt-3"
                        data={{
                            average_star_rating: data?.average_star_rating || 0,
                            checkouts_completed: data?.checkouts_completed || 0,
                            checkouts_total: data?.checkouts_total || 0,
                            total: data?.total || 0,
                        }}
                        type="employee"
                    />
                    <EmployeeList
                        data={data}
                        dateStart={dateStart}
                        dateEnd={dateEnd}
                        pagination
                        onRowClick={handleRowClick}
                        onSelectMenu={handleSelectMenu}
                        onReport={handlePerformanceReport}
                        onScorecardDetail={handleDetail}
                        onPagination={(e) => setPage(e)}
                        onPer={(e) => setPerPage(e)}
                        onSort={handleSortEmployees}
                        isReport
                    />
                </Tab>
                <Tab eventKey="chart" active={active}>
                    <AverageScore className="mt-3" data={actuals?.totals} type="org" />
                    <OrgChart
                        data={actuals}
                        companyId={companyId}
                        dateStart={dateStart}
                        dateEnd={dateEnd}
                        onRowClick={handleRowClick}
                        onReport={handlePerformanceReport}
                        onSelectMenu={handleSelectMenu}
                        onScorecardDetail={handleDetail}
                    />
                </Tab>
            </div>
        </ErrorBoundary>
    );
};

export default CheckoutReport;
